'use client';

import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';

import { cn } from '@/lib/utils';
import { ChevronDownIcon, Plus } from 'lucide-react';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> & {
    theme?: 'LIGHT' | 'DARK';
  }
>(({ className, theme, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(' rounded-[20px] ', className)}
    style={{
      backgroundColor: theme
        ? theme === 'LIGHT'
          ? 'rgba(31, 31, 31, 0.03)'
          : 'rgba(255, 255, 255, 0.1)'
        : undefined,
    }}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    chevronColor?: string;
    hideChevron?: boolean;
    isChevron?: boolean;
    fillForm?: boolean;
  }
>(
  (
    {
      className,
      children,
      chevronColor,
      hideChevron = false,
      isChevron = false,
      fillForm = false,
      ...props
    },
    ref,
  ) => (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cn(
          'flex flex-1 items-center justify-between p-4 rounded-[20px] text-base font-medium transition-all',
          isChevron ? '[&[data-state=open]>svg]:rotate-180' : '[&[data-state=open]>svg]:rotate-45',
          className,
        )}
        {...props}
      >
        {children}

        {!hideChevron &&
          (isChevron ? (
            <ChevronDownIcon
              className="size-6 shrink-0 transition-transform duration-200 text-black"
              style={chevronColor ? { color: chevronColor } : undefined}
            />
          ) : (
            <Plus
              className="size-6 shrink-0 transition-transform duration-200 text-highlighted"
              style={chevronColor ? { color: chevronColor } : undefined}
            />
          ))}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  ),
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-sm  transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn('p-4 pt-2  ', className)}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
